/**
 * React Query hooks for patch management (follows useDeployments.ts conventions).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { patchingApi, PatchApiError } from '@/services/patching'
import { PATCH_POLL_INTERVAL_MS, hasRunningStage } from '@/utils/patchStage'
import { isValidCampaignId } from '@/utils/patchValidation'
import type {
  AdvisorResult,
  ApproveStageBody,
  CreatePatchCampaignRequest,
  ExecuteStageBody,
  PatchAuditEvent,
  PatchCampaign,
  PatchEnvironment,
  PatchMe,
  PatchScheduleRow,
  PostPatchCheck,
  PrePatchCheck,
  RejectStageBody,
  ScheduleStageBody,
  TagResolution,
  VmTag,
} from '@/types/patching'
import type { PatchCampaignList } from '@/services/patching'

export const patchingKeys = {
  all: ['patching'] as const,
  me: () => [...patchingKeys.all, 'me'] as const,
  lists: () => [...patchingKeys.all, 'campaigns'] as const,
  details: () => [...patchingKeys.all, 'campaign'] as const,
  detail: (campaignId: string) => [...patchingKeys.details(), campaignId] as const,
  audit: (campaignId: string) => [...patchingKeys.all, 'audit', campaignId] as const,
  tags: () => [...patchingKeys.all, 'tags'] as const,
  tagResolution: (tagId: number) => [...patchingKeys.all, 'tag-resolution', tagId] as const,
}

/** Don't retry 4xx — a 403/404/409 will not fix itself and the message should show immediately. */
function retryUnlessClientError(failureCount: number, error: unknown): boolean {
  if (error instanceof PatchApiError && error.status !== null && error.status >= 400 && error.status < 500) {
    return false
  }
  return failureCount < 2
}

export function usePatchMe() {
  return useQuery<PatchMe, PatchApiError>({
    queryKey: patchingKeys.me(),
    queryFn: () => patchingApi.getMe(),
    retry: retryUnlessClientError,
  })
}

/** Every tag, across Azure connections. Only fetched when a campaign environment is set to target a tag. */
export function usePatchTags(enabled = true) {
  return useQuery<VmTag[], PatchApiError>({
    queryKey: patchingKeys.tags(),
    queryFn: () => patchingApi.listTags(),
    enabled,
    staleTime: 0,
    retry: retryUnlessClientError,
  })
}

/** The tag's VMs with their live state: the read-only preview in the campaign form. */
export function useTagResolution(tagId: number | undefined) {
  return useQuery<TagResolution, PatchApiError>({
    queryKey: patchingKeys.tagResolution(tagId ?? 0),
    queryFn: () => patchingApi.resolveTag(tagId as number),
    enabled: tagId !== undefined,
    staleTime: 0,
    retry: retryUnlessClientError,
  })
}

export function usePatchCampaigns() {
  return useQuery<PatchCampaignList, PatchApiError>({
    queryKey: patchingKeys.lists(),
    queryFn: () => patchingApi.listCampaigns(),
    staleTime: 0,
    retry: retryUnlessClientError,
  })
}

/**
 * Campaign detail. Polls every PATCH_POLL_INTERVAL_MS only while some stage is in a
 * running status (plan_dispatched/plan_running/execute_dispatched/executing/validating)
 * and stops as soon as every stage is waiting or terminal.
 */
export function usePatchCampaign(campaignId: string | undefined) {
  return useQuery<PatchCampaign, PatchApiError>({
    queryKey: patchingKeys.detail(campaignId ?? ''),
    queryFn: () => patchingApi.getCampaign(campaignId as string),
    enabled: isValidCampaignId(campaignId),
    staleTime: 0,
    retry: retryUnlessClientError,
    refetchInterval: (query) => (hasRunningStage(query.state.data) ? PATCH_POLL_INTERVAL_MS : false),
  })
}

export function usePatchAudit(campaignId: string | undefined, enabled: boolean) {
  return useQuery<PatchAuditEvent[], PatchApiError>({
    queryKey: patchingKeys.audit(campaignId ?? ''),
    queryFn: () => patchingApi.getAudit(campaignId as string),
    enabled: enabled && isValidCampaignId(campaignId),
    staleTime: 0,
    retry: retryUnlessClientError,
  })
}

export function useCreatePatchCampaign() {
  const queryClient = useQueryClient()
  return useMutation<PatchCampaign, PatchApiError, CreatePatchCampaignRequest>({
    mutationFn: (body) => patchingApi.createCampaign(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patchingKeys.lists() })
    },
  })
}

interface StageVars {
  campaignId: string
  env: PatchEnvironment
}

/** Every stage action changes the campaign, the list and the audit trail. */
function useInvalidateCampaign() {
  const queryClient = useQueryClient()
  return (campaignId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: patchingKeys.detail(campaignId) }),
      queryClient.invalidateQueries({ queryKey: patchingKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: patchingKeys.audit(campaignId) }),
    ])
}

export function usePlanStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<void, PatchApiError, StageVars>({
    mutationFn: ({ campaignId, env }) => patchingApi.planStage(campaignId, env),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useApproveStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<void, PatchApiError, StageVars & { body: ApproveStageBody }>({
    mutationFn: ({ campaignId, env, body }) => patchingApi.approveStage(campaignId, env, body),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useRejectStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<void, PatchApiError, StageVars & { body: RejectStageBody }>({
    mutationFn: ({ campaignId, env, body }) => patchingApi.rejectStage(campaignId, env, body),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useExecuteStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<void, PatchApiError, StageVars & { body: ExecuteStageBody }>({
    mutationFn: ({ campaignId, env, body }) => patchingApi.executeStage(campaignId, env, body),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useCancelStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<void, PatchApiError, StageVars>({
    mutationFn: ({ campaignId, env }) => patchingApi.cancelStage(campaignId, env),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useScheduleStage() {
  const invalidate = useInvalidateCampaign()
  return useMutation<PatchScheduleRow, PatchApiError, StageVars & { body: ScheduleStageBody }>({
    mutationFn: ({ campaignId, env, body }) => patchingApi.scheduleStage(campaignId, env, body),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function useCancelSchedule() {
  const invalidate = useInvalidateCampaign()
  return useMutation<PatchScheduleRow, PatchApiError, StageVars & { scheduleId: number }>({
    mutationFn: ({ campaignId, env, scheduleId }) => patchingApi.cancelSchedule(campaignId, env, scheduleId),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function usePrevalidate() {
  const invalidate = useInvalidateCampaign()
  return useMutation<PrePatchCheck[], PatchApiError, StageVars>({
    mutationFn: ({ campaignId, env }) => patchingApi.prevalidate(campaignId, env),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

export function usePostvalidate() {
  const invalidate = useInvalidateCampaign()
  return useMutation<PostPatchCheck[], PatchApiError, StageVars>({
    mutationFn: ({ campaignId, env }) => patchingApi.postvalidate(campaignId, env),
    onSettled: (_data, _err, { campaignId }) => invalidate(campaignId),
  })
}

/** Advisory only — the result never feeds an approval or a gate. */
export function useRunAdvisor() {
  const queryClient = useQueryClient()
  return useMutation<AdvisorResult, PatchApiError, string>({
    mutationFn: (campaignId) => patchingApi.runAdvisor(campaignId),
    onSettled: (_data, _err, campaignId) => {
      queryClient.invalidateQueries({ queryKey: patchingKeys.audit(campaignId) })
    },
  })
}
